import { useState } from "react";
import { Brand } from "../components/Brand";
import { Banner } from "../components/ui";
import { ApiError, type Api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Shown when an authenticated identity (password or Google) has no matching
 * portal account association. Access is never silently granted. §4.1
 *
 * Because the identity is already authenticated here (e.g. after Google sign-in),
 * this is also the registration entry point: the user can submit a self-service
 * access request that support/admin approves out-of-band. §scope change
 */
export function NotProvisioned({
  api,
  email,
  displayName,
}: {
  api: Api;
  email?: string;
  displayName?: string;
}) {
  const { signOut } = useAuth();
  const [name, setName] = useState(displayName ?? "");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError("We couldn't read your email from this sign-in. Contact support.");
      return;
    }
    setBusy(true);
    try {
      await api.submitAccessRequest({
        email,
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        message: message.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not submit your request. Try again later.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Brand />
        </div>
        <div className="card p-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
            Access pending
          </div>
          <h1 className="mt-1 text-xl font-semibold text-ink">
            Your account isn't provisioned yet
          </h1>
          <p className="mt-3 text-sm text-ink-muted">
            {email ? (
              <>
                We authenticated <span className="font-mono text-ink">{email}</span>, but it isn't
                associated with a PumaMesh customer account yet.
              </>
            ) : (
              <>Your sign-in isn't associated with a PumaMesh customer account yet.</>
            )}
          </p>

          {submitted ? (
            <div className="mt-6 space-y-4">
              <Banner tone="success">
                Request received. Our team will review it and enable access for{" "}
                <span className="font-mono">{email}</span>. You can sign in again once approved.
              </Banner>
            </div>
          ) : (
            <>
              <p className="mt-4 text-sm text-ink-muted">
                Request access below and our team will review it, or contact support directly.
              </p>
              {error && (
                <div className="mt-4">
                  <Banner tone="error">{error}</Banner>
                </div>
              )}
              <form onSubmit={onRequest} className="mt-4 space-y-4 text-left">
                <div>
                  <label className="label" htmlFor="np-name">
                    Full name
                  </label>
                  <input
                    id="np-name"
                    type="text"
                    autoComplete="name"
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="np-company">
                    Organization
                  </label>
                  <input
                    id="np-company"
                    type="text"
                    autoComplete="organization"
                    className="input"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="np-message">
                    Reason for access (optional)
                  </label>
                  <textarea
                    id="np-message"
                    className="input min-h-[80px]"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                <button className="btn-primary w-full" type="submit" disabled={busy}>
                  {busy ? "Submitting…" : "Request access"}
                </button>
              </form>
              <a
                href="https://support.pumamesh.com"
                className="mt-4 block text-center text-sm text-ink-muted underline-offset-4 hover:text-accent-strong hover:underline"
              >
                Contact support
              </a>
            </>
          )}

          <button
            className="mt-4 block w-full text-sm text-ink-muted underline-offset-4 hover:text-accent-strong hover:underline"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
